import React, { useMemo } from 'react';
import styled from 'styled-components';
import Lottie from 'lottie-react';
import { motion, useReducedMotion } from 'framer-motion';

export type PlanId = 'marketing' | 'digital-services' | 'ecommerce-digital-services';

type Props = {
  planId: PlanId;
  /** Optional override for plans that use a Lottie. */
  animationData?: unknown;
  /** Small badge in the top‑right. Defaults to "Current plan". */
  badgeText?: string;
  className?: string;
};

/* Brand gradients to keep parity with your existing cards */
const PLAN_GRADIENT: Record<PlanId, string> = {
  marketing: 'linear-gradient(135deg, #ff6a00 0%, #ff345b 50%, #ff0088 100%)',
  'digital-services': 'linear-gradient(135deg, #8c54ff 0%, #3d93ff 50%, #00d1b2 100%)',
  'ecommerce-digital-services': 'linear-gradient(135deg, #34d399 0%, #10b981 50%, #059669 100%)',
};

const Shell = styled.article<{ $gradient: string }>`
  position: relative;
  width: 100%;
  max-width: 420px;
  height: 260px;
  border-radius: 20px;
  overflow: hidden;
  background: ${({ $gradient }) => $gradient};
  box-shadow: 0 20px 60px rgba(0,0,0,.30);
`;

/* Shared full-bleed layer for animated backgrounds */
const Layer = styled.div`
  position: absolute; inset: 0;
  pointer-events: none;
  z-index: 0;
`;

/* Lottie backdrop */
const BgLottie = styled(Lottie)`
  width: 115%;
  height: 115%;
  opacity: .95;
  transform: translateY(-2%);
`;

/* Simplified “glass” so animation stays visible */
const Glass = styled.div`
  position: absolute; inset: 0;
  z-index: 1;
  background: radial-gradient(120% 100% at 50% 0%, rgba(0,0,0,.04) 0%, rgba(0,0,0,.16) 100%);
  mix-blend-mode: multiply;
`;

/* Fallback ambient glow */
const FallbackGlow = styled.div`
  position: absolute; inset: 0; pointer-events: none;
  &:before, &:after {
    content: ''; position: absolute; border-radius: 50%;
    filter: blur(28px); opacity: .28;
  }
  &:before { width: 280px; height: 280px; left: -40px; bottom: -40px; background: #ffffff; }
  &:after  { width: 220px; height: 220px; right: -30px; top: -30px;  background: #ffffff; }
`;

/* “Current plan” badge */
const Badge = styled.span`
  position: absolute; top: 10px; right: 10px; z-index: 2;
  padding: .28rem .6rem; border-radius: 999px;
  background: linear-gradient(180deg, #A7F3D0, #6EE7B7);
  color: #052e1a; font-weight: 900; font-size: .75rem;
  border: 1px solid rgba(0,0,0,.15);
`;

/* ───────── Digital Services background (chips marquee) ───────── */

const Chip = styled(motion.span)`
  position: absolute;
  transform: translateY(-50%);
  background: rgba(255,255,255,0.30);
  padding: 0.5rem 1.2rem;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  backdrop-filter: blur(2px);
`;

const makeMarquee = (duration = 10, delay = 0) => ({
  x: ['120%', '-120%'],
  opacity: [0, 1, 1, 0],
  transition: { duration, delay, ease: 'linear' as const, repeat: Infinity, repeatType: 'loop' as const },
});

function DigitalChipsBg() {
  const reduce = useReducedMotion();

  // Match the original card’s chip rows & pacing
  const chipsA = useMemo(() => ([
    { label: 'Honor any request', y: 24,  delay: 0.2, dur: 11 },
    { label: 'Unlimited tickets', y: 72,  delay: 1.1, dur: 10 },
    { label: 'Design',            y: 118, delay: 0.5, dur: 12 },
    { label: 'Development',       y: 162, delay: 1.6, dur: 13 },
    { label: 'Landing pages',     y: 48,  delay: 2.2, dur: 12 },
  ]), []);

  const chipsB = useMemo(() => ([
    { label: 'Web apps',        y: 86,  delay: 3.2, dur: 11 },
    { label: 'Brand assets',    y: 132, delay: 2.7, dur: 10 },
    { label: 'Email templates', y: 26,  delay: 3.8, dur: 12 },
    { label: 'UI • UX',         y: 170, delay: 4.4, dur: 13 },
  ]), []);

  return (
    <Layer aria-hidden>
      {[...chipsA, ...chipsB].map((c, i) => (
        <Chip
          key={i}
          style={{ top: c.y }}
          initial={reduce ? { opacity: 1 } : { x: '120%', opacity: 0 }}
          animate={reduce ? { opacity: 1 } : makeMarquee(c.dur, c.delay)}
          whileHover={{ scale: 1.08 }}
        >
          {c.label}
        </Chip>
      ))}
    </Layer>
  );
}








/* ───────── NEW: Marketing background that mirrors MarketingServicesCard ───────── */

const MCard = styled(motion.div)`
  position: absolute;
  width: 180px;
  height: 220px;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(0,0,0,.20);
  overflow: hidden;
`;

const MHead = styled.div<{ $bg: string }>`
  height: 54px; background: ${({ $bg }) => $bg};
  display: flex; align-items: center; gap: 8px; padding: 0 12px;
`;

const MBar = styled.div`
  height: 6px; flex: 1; border-radius: 4px; background: rgba(255,255,255,.4);
`;

const MBody = styled.div`
  padding: 12px; display: grid; gap: 8px;
`;

const MBlock = styled.div<{ $h: number; $bg?: string }>`
  height: ${({ $h }) => $h}px;
  border-radius: 8px;
  background: ${({ $bg }) => $bg ?? '#f0f0f0'};
`;

function MarketingStackBg() {
  const reduce = useReducedMotion();

  return (
    <Layer aria-hidden>
      {/* front card */}
      <MCard
        style={{ left: '18%', top: '8%', zIndex: 3 }}
        initial={reduce ? { opacity: 1 } : { y: 24, rotate: -10, opacity: 0 }}
        animate={reduce ? { opacity: 1 } : { y: [0, -6, 0], rotate: [-10, -8, -10], opacity: 1,
          transition: { duration: 6.5, ease: 'easeInOut', repeat: Infinity, repeatType: 'reverse' } }}
      >
        <MHead $bg="linear-gradient(135deg,#667eea,#764ba2)">
          <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(255,255,255,.35)' }} />
          <MBar /><MBar style={{ maxWidth: 48 }} />
        </MHead>
        <MBody>
          <MBlock $h={100} $bg="linear-gradient(135deg,#f093fb,#f5576c)" />
          <MBlock $h={10} /><MBlock $h={10} /><MBlock $h={10} />
        </MBody>
      </MCard>

      {/* middle card */}
      <MCard
        style={{ left: '48%', top: '16%', zIndex: 2 }}
        initial={reduce ? { opacity: 1 } : { y: 30, rotate: 4, opacity: 0 }}
        animate={reduce ? { opacity: 1 } : { y: [0, -10, 0], rotate: [4, 6, 4], opacity: 1,
          transition: { duration: 7, ease: 'easeInOut', repeat: Infinity, repeatType: 'reverse' } }}
      >
        <MHead $bg="linear-gradient(135deg,#4facfe,#00f2fe)">
          <MBar /><div style={{ width: 14, height: 14, borderRadius: 4, background: 'rgba(255,255,255,.35)' }} />
        </MHead>
        <MBody>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 6, height: 72 }}>
            <MBlock $h={34} /><MBlock $h={34} /><MBlock $h={34} /><MBlock $h={34} />
          </div>
          <MBlock $h={10} /><MBlock $h={14} $bg="#4facfe" />
        </MBody>
      </MCard>

      {/* back card */}
      <MCard
        style={{ left: '70%', top: '24%', zIndex: 1 }}
        initial={reduce ? { opacity: 1 } : { y: 40, rotate: 12, opacity: 0 }}
        animate={reduce ? { opacity: 1 } : { y: [0, -8, 0], rotate: [12, 14, 12], opacity: 1,
          transition: { duration: 8, ease: 'easeInOut', repeat: Infinity, repeatType: 'reverse' } }}
      >
        <MHead $bg="linear-gradient(135deg,#fa709a,#fee140)">
          <div style={{ width: 36, height: 6, borderRadius: 3, background: 'rgba(255,255,255,.4)' }} />
          <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'rgba(255,255,255,.3)' }} />
        </MHead>
        <MBody>
          <MBlock $h={34} $bg="linear-gradient(135deg,#a8edea,#fed6e3)" />
          <MBlock $h={10} /><MBlock $h={10} /><MBlock $h={10} />
          <MBlock $h={50} $bg="#fed6e3" />
        </MBody>
      </MCard>
    </Layer>
  );
}


/* ───────── Component ───────── */

export default function ActivePlanPreview({
  planId,
  animationData,
  badgeText = 'Current plan',
  className,
}: Props) {
  // Provide default Lottie only for the ecommerce plan (matches your original)
  let defaultAnimation: unknown | undefined;
  if (planId === 'ecommerce-digital-services') {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    defaultAnimation = require('../../assets/ecommerceFlow.json');
  }
  const data = animationData ?? defaultAnimation;

  return (
    <Shell className={className} $gradient={PLAN_GRADIENT[planId]} aria-label="Plan preview">
      {/* Background per plan */}
      {planId === 'digital-services' ? (
        <DigitalChipsBg />
      ) : planId === 'marketing' ? (
        <MarketingStackBg />
      ) : data ? (
        <Layer>
          <BgLottie
            animationData={data}
            loop
            autoplay
            rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
          />
        </Layer>
      ) : (
        <FallbackGlow />
      )}

      <Glass />
      <Badge>{badgeText}</Badge>
    </Shell>
  );
}
